import { startCase } from "lodash";
import { FC, useContext, useEffect, useState } from "react";
import { Timeline, TimelineEvent } from "react-event-timeline";

import { formatDateTime, formatUnconvertedDateCustom } from "@app/shared";
import { SpecialtyGroupException, SpecialtyGroupExceptionHistory, SpecialtyGroupExceptionStatus } from "@app/projects/results/common";
import SpecialtyGroupExceptionsService from "@app/projects/specialtyGroupExceptionsService";
import { FeatureFlagContext, Features } from "@app/feature-flags";
import { SpecialtyGroupExceptionGrade } from "@app/projects/results/common/specialtyGroupException";

interface SpecialtyGroupExceptionTimelineProps {
  projectId?: number;
  regionSetSpecialtyGroupId?: number;
  specialtyGroupException?: SpecialtyGroupException;
}

export const SpecialtyGroupExceptionHistoryTimeline: FC<SpecialtyGroupExceptionTimelineProps> = (props: SpecialtyGroupExceptionTimelineProps) => {
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;

  const specialtyGroupExceptionsService = new SpecialtyGroupExceptionsService();

  const [specialtyGroupExceptionHistories, setSpecialtyGroupExceptionHistories] = useState<SpecialtyGroupExceptionHistory[]>([]);

  useEffect(() => {
    loadSpecialtyExceptionHistories();
  }, []);

  const loadSpecialtyExceptionHistories = async () => {
    let specialtyGroupExceptionHistories: SpecialtyGroupExceptionHistory[];
    if (props.specialtyGroupException) {
      specialtyGroupExceptionHistories = await specialtyGroupExceptionsService.getSpecialtyGroupExceptionHistory(
        props.specialtyGroupException.customProjectId,
        props.specialtyGroupException.regionSetId,
        props.specialtyGroupException.specialtyGroupCode
      );
    } else {
      specialtyGroupExceptionHistories = await specialtyGroupExceptionsService.getSpecialtyGroupExceptionHistoryById(
        props.projectId,
        props.regionSetSpecialtyGroupId
      );
    }

    setSpecialtyGroupExceptionHistories(specialtyGroupExceptionHistories);
  }

  const renderException = (): React.ReactNode => {
    if (!specialtyGroupExceptionHistories) {
      return null;
    }

    //the TimelineEvent component unfortunately uses inline
    //styling props instead of className props for its sub-elements
    const contentStyle = {
      fontSize: "100%",
      fontWeight: "normal",
    };
    const iconStyle = {
      cursor: "default",
    };
    const bubbleStyle = {
      background: "#fff",
      border: "none",
    };

    return (
      <>
        <div className="history-timeline-container">
          <Timeline>
            {specialtyGroupExceptionHistories.map((specialtyGroupExceptionHistory) => {
              let iconClass = "fa-minus-circle history-icon-warning";
              switch (specialtyGroupExceptionHistory.specialtyGroupExceptionStatus) {
                case SpecialtyGroupExceptionStatus.Approved:
                  iconClass = "fa-check-circle history-icon-success";
                  break;
                case SpecialtyGroupExceptionStatus.Denied:
                  iconClass = "fa-times-circle history-icon-danger";
                  break;
                case SpecialtyGroupExceptionStatus.Archived:
                  iconClass = "fa-trash history-icon-danger";
                  break;
              }

              return (
                <TimelineEvent
                  key={`${specialtyGroupExceptionHistory.specialtyGroupExceptionStatus}:${specialtyGroupExceptionHistory
                    .dateUpdated}`}
                  title={<strong>{`Updated by ${!specialtyGroupExceptionHistory.updatedByUserFullName || specialtyGroupExceptionHistory.updatedByUserFullName === "Quest Analytics" ? "System" : specialtyGroupExceptionHistory.updatedByUserFullName} on`}</strong>}
                  subtitle={<strong>{`${formatDateTime(specialtyGroupExceptionHistory.dateUpdated)}`}</strong>}
                  iconStyle={iconStyle}
                  bubbleStyle={bubbleStyle}
                  icon={<i className={`fa ${iconClass} fs22`} />}
                  style={contentStyle}
                  contentStyle={contentStyle}
                >
                  <div className="container timeline-container">
                    <div className="row mb5">
                      <div className="col-12 col-lg-3">
                        <strong>Status:</strong>
                      </div>
                      <div className="col-12 col-lg">
                        {startCase(specialtyGroupExceptionHistory.specialtyGroupExceptionStatus)}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-lg-3">
                        <strong>Start Date:</strong>
                      </div>
                      <div className="col-12 col-lg">
                        {specialtyGroupExceptionHistory.startDate ? formatUnconvertedDateCustom(specialtyGroupExceptionHistory.startDate, "MM/DD/YYYY") : '---'}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-lg-3">
                        <strong>Expiration Date:</strong>
                      </div>
                      <div className="col-12 col-lg">
                        {specialtyGroupExceptionHistory.endDate ? formatUnconvertedDateCustom(specialtyGroupExceptionHistory.endDate, "MM/DD/YYYY") : '---'}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-lg-3">
                        <strong>Grade:</strong>
                      </div>
                      <div className="col-12 col-lg">
                        {specialtyGroupExceptionHistory.grade === SpecialtyGroupExceptionGrade.None ? '---' : startCase(specialtyGroupExceptionHistory.grade)}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-lg-3">
                        <strong>Comment:</strong>
                      </div>
                      <div className="col-12 col-lg">
                        {specialtyGroupExceptionHistory.comment}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-lg-3">
                        <strong>Processed:</strong>
                      </div>
                      <div className="col-12 col-lg">
                        {specialtyGroupExceptionHistory.isUnprocessed ? 'No' : 'Yes'}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-lg-3">
                        <strong>Exception Narrative:</strong>
                      </div>
                      <div className="col-12 col-lg">
                        {specialtyGroupExceptionHistory.exceptionNarrative}
                      </div>
                    </div>
                    {specialtyGroupExceptionHistory.supplementaryDocuments.length > 0 &&
                      featureFlags.has(Features.VOLUME_EXCEPTIONS_SUPPLEMENTARY_DOCUMENTS) &&
                      (
                        <div className="row">
                          <div className="col-12 col-lg-3">
                            <strong>Attachments:</strong>
                          </div>
                          <div className="col-12 col-lg">
                            <ul>
                              {
                                specialtyGroupExceptionHistory.supplementaryDocuments.map((document) =>
                                  <li key={`${document.specialtyGroupExceptionId}_${document.supplementaryDocumentMetadataId}`}>
                                    {document.documentName}
                                  </li>)
                              }
                            </ul>
                          </div>
                        </div>
                      )}
                  </div>
                </TimelineEvent>
              );
            })}
          </Timeline>
        </div>
      </>
    );
  };

  return renderException();

}
